import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../services/adminService';
import { Modal } from '../shared/Modal';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import './AdminPanel.css';

export function RegistrationQueue() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReg, setSelectedReg] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useAbortableFetch(({ isCancelled }) => {
    (async () => {
      try {
        const data = await adminService.getPendingRegistrations();
        if (isCancelled()) return;
        setRegistrations(data);
      } catch (err) {
        if (isCancelled()) return;
        setError(mapSupabaseError(err));
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    })();
  }, []);

  const handleApprove = async (reg) => {
    setActionLoading(true);
    try {
      await adminService.approveRegistration(reg.id, reg);
      setRegistrations((prev) => prev.filter((r) => r.id !== reg.id));
      toast.success(`Approved ${reg.email}`);
    } catch (err) {
      setError(mapSupabaseError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await adminService.rejectRegistration(selectedReg.id, rejectionReason);
      setRegistrations((prev) => prev.filter((r) => r.id !== selectedReg.id));
      toast.success(`Rejected ${selectedReg.email}`);
      setSelectedReg(null);
      setRejectionReason('');
    } catch (err) {
      setError(mapSupabaseError(err));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <p>Loading registrations...</p>;

  return (
    <div className="registration-queue">
      <h2>Pending Registrations</h2>

      {error && <div className="error-message" role="alert">{error}</div>}

      {registrations.length === 0 ? (
        <p>No pending registrations</p>
      ) : (
        <table className="registrations-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Position</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {registrations.map((reg) => (
              <tr key={reg.id}>
                <td>{reg.name}</td>
                <td>{reg.email}</td>
                <td>{reg.position}</td>
                <td>{new Date(reg.submitted_at).toLocaleDateString()}</td>
                <td>
                  <button
                    onClick={() => handleApprove(reg)}
                    className="btn-approve"
                    disabled={actionLoading}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => setSelectedReg(reg)}
                    className="btn-reject"
                    disabled={actionLoading}
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedReg && (
        <Modal onClose={() => setSelectedReg(null)}>
          <h3>Reject Registration</h3>
          <p>Rejecting: {selectedReg.name} ({selectedReg.email})</p>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Reason for rejection..."
            rows="4"
            maxLength={500}
          />
          {error && <div className="error-message" role="alert">{error}</div>}
          <div className="modal-buttons">
            <button
              onClick={handleReject}
              className="btn-primary"
              disabled={actionLoading}
            >
              {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
            </button>
            <button
              onClick={() => setSelectedReg(null)}
              className="btn-secondary"
              disabled={actionLoading}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
